import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Avatar, Box, Button, Toolbar, Typography, useMediaQuery } from "@mui/material";
import { AutoAwesomeOutlined, HistoryOutlined } from "@mui/icons-material";
import { PersonAdd } from "@mui/icons-material";
import { GppMaybeOutlined } from "@mui/icons-material";
import { GoogleLogin } from "@react-oauth/google";
import { hasExplicitLogout } from "../../universal/explicitLogoutGuard";
import { useLocation } from "react-router-dom";
import Tooltip from "../common/Tooltip";
import utils from "../../universal/utils";
import {
    pickAvatarUpdatedAtMs,
    resolveAvatarSrc,
} from "../../utils/avatarUrl";
import { colors } from "../../theme/colors";
import AvatarContextMenu from "./AvatarContextMenu";
import ConfirmDialog from "../common/ConfirmDialog.jsx";
import { markShellIntroComplete, shouldPlayShellIntro } from "./shellIntroMotion";
import { PageContainer } from "./PageContainer";
import TopBarActionButton, {
    TOPBAR_ACTION_ICON_SX,
    TopBarNotificationAction,
} from "./TopBarActionButton";
import { motion } from "framer-motion";
import { useLobbyReviewPanelBridge } from "../lobby/LobbyReviewPanelBridgeContext";
import useUiDesignActions from "../app/useUiDesignActions";
import {
    MOBILE_NAV_MEDIA,
    TOPBAR_HEIGHT_PX,
    TOPBAR_HEIGHT_SX,
    TOPBAR_XL_HEIGHT_PX,
} from "./layoutConstants";

const MotionBox = motion(Box);

const basePath = utils.getEdgeBasePath();

export default function TopBar({
    isAuthenticated,
    autoLoginInProgress = false,
    googleResponse,
    actions,
    onFailure,
    user = {},
    catalog = {},
    logout,
}) {
    const location = useLocation();
    const isMobileNav = useMediaQuery(MOBILE_NAV_MEDIA);
    const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
    const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
    const avatarAnchorRef = useRef(null);
    const previousAvatarMenuOpen = useRef(avatarMenuOpen);
    const [countryFlag, setCountryFlag] = useState(null);
    const {
        isPendingReviewPanelOpen,
        openPendingReviewPanel,
    } = useLobbyReviewPanelBridge();

    const profileUser = catalog?.user || user;
    const {
        pending: uiDesignPending,
        showSwitchToLegacy,
        showSwitchToV2,
        switchToLegacy,
        switchToV2,
    } = useUiDesignActions({
        user: profileUser,
        actions,
    });

    const designSwitchButtonSx = {
        textTransform: "none",
        borderRadius: "12px",
        borderColor: "rgba(255,255,255,0.08)",
        color: colors.text.secondary,
        fontSize: "0.78rem",
        fontWeight: 600,
        px: 1.25,
        py: 0.5,
        minHeight: { xs: "2.6rem", xl: "2.8rem" },
        whiteSpace: "nowrap",
        "&:hover": {
            borderColor: "rgba(232,163,61,0.18)",
            color: colors.primary.main,
            backgroundColor: "rgba(255,255,255,0.03)",
        },
    };

    const {
        friendRequests,
        currentLobby,
        currentLobbyCode,
        matchmakingStarted,
    } = catalog;

    const showReviewPanelBadge = !isPendingReviewPanelOpen;
    const playShellIntro = shouldPlayShellIntro();
    const isLeader = user && currentLobby ? currentLobby.hostId === user.userId : false;
    const blocked =
        catalog?.lobbyState === "searching" ||
        (matchmakingStarted && !isLeader && currentLobbyCode);
    const liveAvatarCacheBust =
        Number(catalog?.avatarCacheBust) > 0
            ? Number(catalog.avatarCacheBust)
            : null;
    const avatarVersion = pickAvatarUpdatedAtMs(liveAvatarCacheBust, user);
    const avatarSrc = resolveAvatarSrc({
        basePath,
        picture: user?.picture,
        userId: user?.userId || user?._id,
        avatarUpdatedAtMs: avatarVersion,
        hasCustomAvatar: !!user?.avatar,
    });

    useEffect(() => {
        if (playShellIntro) {
            markShellIntroComplete();
        }
    }, [playShellIntro]);

    useEffect(() => {
        actions?.getTrackingVisitorId?.();
    }, [actions, user?._id]);

    useEffect(() => {
        let cancelled = false;

        async function fetchCountryFlag() {
            if (user?.flagImageUrl) {
                setCountryFlag(user.flagImageUrl);
                return;
            }

            if (!user?.country) {
                setCountryFlag(null);
                return;
            }

            try {
                const response = await fetch(`${basePath}/api/v1/lobby/country-flag/${user.country}`);
                const data = await response.json();

                if (!cancelled && data.ok) {
                    setCountryFlag(data.country.flagImageUrl);
                }
            } catch (error) {
                console.error("Failed to fetch country flag:", error);
            }
        }

        fetchCountryFlag();

        return () => {
            cancelled = true;
        };
    }, [user?.country, user?.flagImageUrl]);

    useEffect(() => {
        if (previousAvatarMenuOpen.current === true && avatarMenuOpen === false) {
            avatarAnchorRef.current?.focus();
        }

        previousAvatarMenuOpen.current = avatarMenuOpen;
    }, [avatarMenuOpen]);

    function handleAvatarMenuToggle() {
        setAvatarMenuOpen((previousOpen) => !previousOpen);
    }

    function handleAvatarMenuClose(event) {
        if (
            event?.target &&
            avatarAnchorRef.current?.contains(event.target)
        ) {
            return;
        }

        setAvatarMenuOpen(false);
    }

    function handleLogout(event) {
        event.preventDefault();
        event.stopPropagation();
        setAvatarMenuOpen(false);
        setLogoutDialogOpen(true);
    }

    function handleConfirmLogout() {
        setLogoutDialogOpen(false);
        logout?.();
    }

    function handleCancelLogout() {
        setLogoutDialogOpen(false);
    }

    return (
        <MotionBox
            component="header"
            initial={playShellIntro ? { y: -18, opacity: 0 } : false}
            animate={{ y: 0, opacity: 1 }}
            transition={{
                duration: 0.55,
                ease: [0.22, 1, 0.36, 1],
            }}
            sx={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                width: "100%",
                zIndex: 1400,
                display: "flex",
                justifyContent: "center",
                background: `
                    linear-gradient(
                        180deg,
                        rgba(8,8,10,0.92) 0%,
                        rgba(8,8,10,0.78) 60%,
                        rgba(8,8,10,0.38) 100%
                    )
                `,
                backdropFilter: "blur(22px)",
                WebkitBackdropFilter: "blur(22px)",
                borderBottom: `1px solid rgba(255,255,255,0.05)`,
                boxShadow: `
                    0 10px 40px rgba(0,0,0,0.45),
                    inset 0 -1px 0 rgba(255,255,255,0.03)
                `,
                "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                    background: `
                        radial-gradient(
                            circle at top,
                            rgba(232,163,61,0.08),
                            transparent 65%
                        )
                    `,
                },
                "&::after": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: "1px",
                    background: `
                        linear-gradient(
                            90deg,
                            transparent,
                            rgba(232,163,61,0.35),
                            transparent
                        )
                    `,
                },
            }}
        >
            <PageContainer>
                <Box sx={{ width: "100%", boxSizing: "border-box" }}>
                    <Toolbar
                        disableGutters
                        sx={{
                            position: "relative",
                            zIndex: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            transition: "all 0.3s ease",
                            height: TOPBAR_HEIGHT_SX,
                            minHeight: {
                                xs: `${TOPBAR_HEIGHT_PX}px !important`,
                                xl: `${TOPBAR_XL_HEIGHT_PX}px !important`,
                            },
                            paddingInline: "0.2rem",
                            width: "100%",
                            boxSizing: "border-box",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: "0.5rem",
                                flexShrink: 0,
                            }}
                        >
                            <Box component="a" href="/" sx={{ position: "relative", display: "flex", alignItems: "center" }}>
                                <Box
                                    component="img"
                                    alt="Sportyshop"
                                    src="https://ouro-public-assets.s3.eu-west-2.amazonaws.com/init-logo-darkmode.png"
                                    sx={{
                                        filter: "drop-shadow(0 0 14px rgba(232,163,61,0.12))",
                                        height: { xs: "1.8rem", sm: "2.2rem" },
                                        display: "block",
                                        backgroundColor: "transparent",
                                        transition: "all 0.3s ease",
                                    }}
                                />
                            </Box>
                            <Box
                                component="span"
                                aria-label="Beta release"
                                sx={{
                                    position: "relative",
                                    isolation: "isolate",
                                    overflow: "hidden",
                                    height: { sm: "1.35rem", xl: "1.48rem" },
                                    ml: "0.45rem",
                                    px: { sm: "0.55rem", xl: "0.62rem" },
                                    display: isMobileNav ? "none" : "flex",
                                    alignItems: "center",
                                    border: "1px solid rgba(232,163,61,0.2)",
                                    borderRadius: "0.42rem",
                                    background: `
                                        linear-gradient(
                                            145deg,
                                            rgba(246,197,111,0.09) 0%,
                                            rgba(232,163,61,0.035) 42%,
                                            rgba(8,8,10,0.72) 100%
                                        )
                                    `,
                                    boxShadow: `
                                        inset 0 1px 0 rgba(255,225,166,0.08),
                                        inset 0 -1px 0 rgba(0,0,0,0.36),
                                        0 4px 12px rgba(0,0,0,0.2),
                                        0 0 14px rgba(232,163,61,0.045)
                                    `,
                                    "&::after": {
                                        content: '""',
                                        position: "absolute",
                                        zIndex: -1,
                                        inset: 0,
                                        background: "linear-gradient(105deg, transparent 10%, rgba(255,232,184,0.07) 48%, transparent 72%)",
                                        pointerEvents: "none",
                                    },
                                }}
                            >
                                <Typography
                                    component="span"
                                    sx={{
                                        color: colors.accent.soft,
                                        fontSize: { sm: "0.6rem", xl: "0.66rem" },
                                        fontWeight: 760,
                                        letterSpacing: "0.15em",
                                        lineHeight: 1,
                                        textShadow: "0 1px 5px rgba(232,163,61,0.14)",
                                    }}
                                >
                                    BETA
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ flex: "1 1 auto", minWidth: 0 }} />

                        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                            {isAuthenticated && user ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        gap: "0.7rem",
                                    }}
                                >
                                    {showSwitchToLegacy && !isMobileNav ? (
                                        <Tooltip
                                            title="Return to the classic Ouro lobby and navigation."
                                            placement="bottom"
                                        >
                                            <Box component="span" sx={{ display: "inline-flex" }}>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    disabled={uiDesignPending}
                                                    onClick={switchToLegacy}
                                                    startIcon={
                                                        <HistoryOutlined sx={{ fontSize: "1rem" }} />
                                                    }
                                                    sx={designSwitchButtonSx}
                                                >
                                                    Legacy layout
                                                </Button>
                                            </Box>
                                        </Tooltip>
                                    ) : null}

                                    {showSwitchToV2 ? (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            disabled={uiDesignPending}
                                            onClick={switchToV2}
                                            startIcon={
                                                <AutoAwesomeOutlined sx={{ fontSize: "1rem" }} />
                                            }
                                            sx={designSwitchButtonSx}
                                        >
                                            New layout
                                        </Button>
                                    ) : null}

                                    {friendRequests?.length > 0 ? (
                                        <TopBarActionButton
                                            aria-label="Friend requests"
                                            badgeContent={friendRequests.length}
                                            onClick={() => {
                                                actions?.setFriendSidebarTab?.("request");
                                                actions?.setFriendSidebarOpen?.(true);
                                            }}
                                        >
                                            <PersonAdd sx={TOPBAR_ACTION_ICON_SX} />
                                        </TopBarActionButton>
                                    ) : null}

                                    {showReviewPanelBadge ? (
                                        <TopBarActionButton
                                            aria-label="Show profile review notice"
                                            badgeContent={1}
                                            badgeTone="info"
                                            onClick={openPendingReviewPanel}
                                        >
                                            <GppMaybeOutlined sx={TOPBAR_ACTION_ICON_SX} />
                                        </TopBarActionButton>
                                    ) : null}

                                    <TopBarNotificationAction
                                        mongoUserId={user?._id}
                                        userId={user?.userId}
                                        routePathname={location.pathname}
                                    />

                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: "0.5rem",
                                        }}
                                    >
                                        {user?._id && (
                                            <MotionBox
                                                component="button"
                                                type="button"
                                                ref={avatarAnchorRef}
                                                aria-label="Open account menu"
                                                aria-haspopup="menu"
                                                aria-expanded={avatarMenuOpen}
                                                whileHover={{
                                                    y: -2,
                                                    scale: 1.02,
                                                }}
                                                whileTap={{ scale: 0.96 }}
                                                transition={{
                                                    duration: 0.22,
                                                    ease: [0.22, 1, 0.36, 1],
                                                }}
                                                sx={{
                                                    position: "relative",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    width: { xs: "2.6rem", xl: "2.8rem" },
                                                    height: { xs: "2.6rem", xl: "2.8rem" },
                                                    flex: "0 0 auto",
                                                    padding: "2px",
                                                    boxSizing: "border-box",
                                                    borderRadius: "14px",
                                                    overflow: "hidden",
                                                    cursor: "pointer",
                                                    color: "inherit",
                                                    font: "inherit",
                                                    background: `conic-gradient(
                                                        from 200deg,
                                                        #F2BE63 0%,
                                                        #FFD27C 12%,
                                                        #D88C25 18%,
                                                        rgba(216,140,37,0.16) 85%,
                                                        #F2BE63 100%
                                                    )`,
                                                    border: 0,
                                                    boxShadow: colors.glow.soft,
                                                    transition:
                                                        "all 220ms cubic-bezier(0.22,1,0.36,1)",
                                                    "&:hover": {
                                                        boxShadow: `
                                                            0 14px 34px rgba(0,0,0,0.45),
                                                            0 0 24px rgba(232,163,61,0.10)
                                                        `,
                                                    },
                                                }}
                                                onClick={handleAvatarMenuToggle}
                                            >
                                                <Avatar
                                                    src={avatarSrc}
                                                    alt={user.nickname || "Account avatar"}
                                                    sx={{
                                                        width: "100%",
                                                        height: "100%",
                                                        borderRadius: "12px",
                                                        border: `1px solid ${colors.background.base}`,
                                                        boxSizing: "border-box",
                                                    }}
                                                />
                                            </MotionBox>
                                        )}
                                    </Box>
                                </Box>
                            ) : autoLoginInProgress ? null : (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                >
                                    <GoogleLogin
                                        width={188}
                                        onSuccess={googleResponse}
                                        onError={onFailure}
                                        auto_select={!hasExplicitLogout()}
                                        useOneTap={!hasExplicitLogout()}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Toolbar>
                </Box>
            </PageContainer>

            <AvatarContextMenu
                open={avatarMenuOpen}
                anchorEl={avatarAnchorRef.current}
                user={profileUser}
                avatarSrc={avatarSrc}
                countryFlag={countryFlag}
                membership={catalog?.membership}
                blocked={Boolean(blocked)}
                actions={actions}
                onClose={handleAvatarMenuClose}
                onLogout={handleLogout}
            />

            <ConfirmDialog
                open={logoutDialogOpen}
                title="Logout"
                message="Are you sure you want to log out?"
                confirmText="Logout"
                cancelText="Cancel"
                onConfirm={handleConfirmLogout}
                onCancel={handleCancelLogout}
            />

        </MotionBox>
    );
}

TopBar.propTypes = {
    isAuthenticated: PropTypes.bool.isRequired,
    autoLoginInProgress: PropTypes.bool,
    googleResponse: PropTypes.func.isRequired,
    onFailure: PropTypes.func.isRequired,
    actions: PropTypes.object,
    catalog: PropTypes.object,
    user: PropTypes.object,
    logout: PropTypes.func,
};
